/**
 * TopTractsList.tsx — the ranked "Top tracts in view" list body (T-222 / BEAD-0040)
 *
 * The reusable *content* of the ranked list: the sort control + the ranked rows, driven by
 * the live ranking hook (useTopTracts → querySourceFeatures). It carries NO surrounding
 * chrome — the desktop slide-out (TopTractsSidePanel) and the mobile drawer supply the
 * title bar and the close/back affordance, reusing the tract-detail (ScorePanel) idiom.
 *
 * Row rendering reuses the panels' language: scoreColor + getTierWord for the headline
 * Opportunity Score, cmpiColor for the inverted Market-Pressure badge. The headline stays the
 * Opportunity Score + tier word (the core number); when a non-default sort is active the sort
 * metric is shown as an explicitly direction-labelled secondary badge so the ordering is never
 * a guess. Clicking a row pans the camera to that tract and drives the SAME selection path a
 * map click uses (onSelectTract) — ScorePanel / ScoreDrillDown are untouched (design §5).
 */
import { useState } from 'react'
import { useMap } from './MapContext'
import { useTopTracts } from '../../hooks/useTopTracts'
import {
  SORT_MODES,
  sortModeById,
  DEFAULT_SORT_MODE_ID,
  type SortModeId,
  type RankedRow,
} from '../../lib/topTracts'
import { scoreColor, cmpiColor } from '../ScoreDrillDown'
import { getTierWord } from '../ScorePanel'
import { featurePropsToTract, dominantTypeLabel, type CensusTract } from '../../lib/tractModel'
import type { MapTier } from '../../lib/mapTiers'

export interface TopTractsListProps {
  /** Current geography tier — the list only ranks at the 'tract' tier (§4.1). */
  tier: MapTier
  /** Score-Tracts layer toggle — when off there is nothing drawn to rank. */
  visible: boolean
  /** Active map tier filter to mirror (decision G1). */
  tierFilter?: string[]
  /** Active map land-use filter to mirror (decision G1). */
  typeFilter?: string[]
  /**
   * Drives the shared tract-selection path (the same one a map click uses). Called with the
   * hydrated CensusTract and the tract centroid the camera eased to.
   */
  onSelectTract: (tract: CensusTract, centroid: [number, number]) => void
  /** Top N to show (default 10, decision E1). */
  limit?: number
}

function ordinal(n: number): string {
  const v = Math.round(n)
  const s = ['th', 'st', 'nd', 'rd']
  const m = v % 100
  return `${v}${s[(m - 20) % 10] ?? s[m] ?? s[0]}`
}

/** One-line context: land-use · Nth percentile in state (decision H1). */
function contextLine(props: RankedRow['props']): string {
  const parts = [dominantTypeLabel(props.dominant_type)]
  if (props.lvi_pctile_state != null) parts.push(`${ordinal(props.lvi_pctile_state)} pct in state`)
  return parts.join(' · ')
}

/** Colour for the secondary sort-metric badge — inverted scale for Market Pressure. */
function badgeColor(modeId: SortModeId, rawValue: number): string {
  const mode = sortModeById(modeId)
  if (mode.inverted) return cmpiColor(rawValue)
  // HCO's raw 0–1 is displayed ×100; colour it on the same displayed scale.
  return scoreColor(mode.displayValue(rawValue))
}

export function TopTractsList({
  tier,
  visible,
  tierFilter,
  typeFilter,
  onSelectTract,
  limit,
}: TopTractsListProps) {
  const map = useMap()
  const [sortModeId, setSortModeId] = useState<SortModeId>(DEFAULT_SORT_MODE_ID)

  const { rows, totalInView } = useTopTracts({
    tier,
    visible,
    sortModeId,
    tierFilter,
    typeFilter,
    limit,
  })

  const active = visible && tier === 'tract'
  const mode = sortModeById(sortModeId)

  function handleRowClick(row: RankedRow) {
    if (map) map.easeTo({ center: row.centroid, duration: 600 })
    onSelectTract(featurePropsToTract(row.props), row.centroid)
  }

  const caption = !active
    ? 'Zoom in to rank tracts in view'
    : rows.length === 0
      ? 'No tracts match the current filters in view'
      : `Top ${rows.length} of ${totalInView} tracts in the current view`

  return (
    <div className="font-sans">
      <div className="px-4 pt-3 pb-2 border-b border-border">
        <p className="text-xs text-muted-foreground">{caption}</p>
        {/* Sort control only when there is something to rank — no dangling dropdown at
            state/county zoom or when filters exclude every tract. */}
        {active && rows.length > 0 && (
          <>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <label htmlFor="toptracts-sort" className="shrink-0">
                Sort by
              </label>
              <select
                id="toptracts-sort"
                value={sortModeId}
                onChange={(e) => setSortModeId(e.target.value as SortModeId)}
                className="min-w-0 flex-1 rounded-md border border-border bg-background text-foreground px-2 py-1 text-xs"
              >
                {SORT_MODES.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-[0.7rem] italic text-muted-foreground mt-1">{mode.directionHint}</p>
          </>
        )}
      </div>

      {active && rows.length > 0 && (
        <ul>
          {rows.map((row) => {
            const lvi = Math.round(row.props.lvi)
            const showBadge = sortModeId !== 'opportunity' && row.sortValue != null
            return (
              <li key={row.geoid}>
                <button
                  type="button"
                  data-testid={`toptract-row-${row.geoid}`}
                  onClick={() => handleRowClick(row)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 border-b border-border/40 text-left hover:bg-accent/60 transition-colors"
                >
                  <span className="w-5 shrink-0 text-right text-xs font-semibold text-muted-foreground tabular-nums">
                    {row.rank}
                  </span>
                  <span
                    className="w-8 shrink-0 text-right text-2xl font-extrabold leading-none tabular-nums"
                    style={{ color: scoreColor(lvi) }}
                  >
                    {lvi}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block text-[0.8rem] font-bold leading-tight"
                      style={{ color: scoreColor(lvi) }}
                    >
                      {getTierWord(lvi)}
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {contextLine(row.props)}
                    </span>
                    <span className="block text-[0.65rem] text-muted-foreground/80">
                      Tract {row.geoid}
                    </span>
                  </span>
                  {showBadge && (
                    <span className="shrink-0 text-right">
                      <span
                        className="block text-sm font-bold leading-none tabular-nums"
                        style={{ color: badgeColor(sortModeId, row.sortValue as number) }}
                      >
                        {mode.displayValue(row.sortValue as number)}
                      </span>
                      <span className="block text-[0.6rem] text-muted-foreground">
                        {mode.badgeLabel}
                      </span>
                    </span>
                  )}
                  <span aria-hidden className="shrink-0 text-muted-foreground">
                    ›
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <p className="px-4 py-2 text-[0.7rem] text-muted-foreground leading-snug">
        Ranked from the tracts currently drawn on the map. Pan or zoom to re-rank.
      </p>
    </div>
  )
}
